import * as cognito from "aws-amplify/auth"
import * as schema from "ipctg-inflow-types/InFlowGatedAPI"

import { post, put } from "@/utilities/fetch++"
import * as rsp from "@/utilities/types/rsp"

const api = {
  auth: {
    confirmSignIn: async (input: cognito.ConfirmSignInInput) => {
      const confirmSignInResult = await cognito.confirmSignIn(input)
      return confirmSignInResult
    },
    confirmSignUp: async (input: cognito.ConfirmSignUpInput) => {
      const confirmSignUpResult = await cognito.confirmSignUp(input)
      return confirmSignUpResult
    },
    isAuthenticated: async (): Promise<boolean> => {
      try {
        return !!(await cognito.getCurrentUser())
      } catch {
        return false
      }
    },
    resendSignUpCode: async (input: cognito.ResendSignUpCodeInput) => {
      const result = await cognito.resendSignUpCode(input)
      return result
    },
    fetchAuthSession: async (): Promise<
      | { cognitoIDToken: string | undefined; error?: never }
      | { cognitoIDToken?: never; error: cognito.AuthError }
    > => {
      try {
        const cognitoIDToken = (
          await cognito.fetchAuthSession()
        ).tokens?.idToken?.toString()
        return { cognitoIDToken }
      } catch (error: any) {
        return { error }
      }
    },
    forgotPassword: async (args: cognito.ResetPasswordInput) => {
      return cognito.resetPassword(args)
    },
    resetPassword: async (input: cognito.ConfirmResetPasswordInput) => {
      return cognito.confirmResetPassword(input)
    },
    signIn: async (
      input: cognito.SignInInput
    ): Promise<cognito.SignInOutput> => {
      const result = await cognito.signIn(input)
      return result
    },
    signUp: async (
      input: cognito.SignUpInput
    ): Promise<cognito.SignUpOutput> => {
      const result = await cognito.signUp(input)
      return result
    },
    autoSignIn: async () => {
      const result = await cognito.autoSignIn()
      return result
    },
    signOut: async (): Promise<void> => {
      return cognito.signOut()
    },
    fetchUserAttributes: async () => {
      return cognito.fetchUserAttributes()
    },
    updateUserAttributes: async (attributes: Record<string, string>) => {
      return cognito.updateUserAttributes({
        userAttributes: attributes,
      })
    },
    updatePassword: async (input: cognito.UpdatePasswordInput) => {
      return cognito.updatePassword(input)
    },
  },
  account: {
    POST: async (request: schema.GatedAccountPOSTRequest) =>
      post<
        | schema.GatedAccountPOSTSuccessResponse
        | schema.GatedAccountPOSTErrorResponse
      >({
        url: "/account",
        data: request,
      }),
  },
  appUser: {
    PUT: async (request: schema.GatedAppUserPUTRequest) =>
      put<
        | schema.GatedAppUserPUTSuccessResponse
        | schema.GatedAppUserPUTErrorResponse
      >({
        url: `/appuser`,
        data: request,
      }),
  },
  autoPaySelectCard: {
    POST: async (request: schema.GatedAutoPaySelectCardPOSTRequest) =>
      post<
        | schema.GatedAutoPaySelectCardPOSTSuccessResponse
        | schema.GatedAutoPaySelectCardPOSTErrorResponse
      >({
        url: "/auto-pay-select-card",
        data: request,
      }),
  },
  checkout: {
    POST: async (request: schema.GatedCheckoutPOSTRequest) =>
      post<rsp.GatedCheckoutResponse>({
        url: `/checkout`,
        data: request,
      }),
  },
  confirmSetupIntent: {
    POST: async (request: schema.GatedConfirmSetupIntentPOSTRequest) =>
      post<rsp.GatedConfirmSetupIntentResponse>({
        url: `/confirm-setup-intent`,
        data: request,
      }),
  },
  confirmSetupIntentWallet: {
    POST: async (request: schema.GatedConfirmSetupIntentWalletPOSTRequest) =>
      post<
        | schema.GatedConfirmSetupIntentWalletPOSTSuccessResponse
        | schema.GatedConfirmSetupIntentWalletPOSTErrorResponse
      >({
        url: `/confirm-setup-intent-wallet`,
        data: request,
      }),
  },
  emailConfirmation: {
    POST: async (request: schema.GatedEmailConfirmationPOSTRequest) =>
      post<rsp.GatedEmailConfirmationResponse>({
        url: `/email-confirmation`,
        data: request,
      }),
  },
  emailReceipt: {
    POST: async (request: schema.GatedEmailReceiptPOSTRequest) =>
      post<rsp.GatedEmailReceiptPOSTResponse>({
        url: `/email-receipt`,
        data: request,
      }),
  },
  getConfirmationDetails: {
    POST: async (request: schema.GatedGetConfirmationDetailsPOSTRequest) =>
      post<rsp.GatedGetConfirmationDetailsResponse>({
        url: `/get-confirmation-details`,
        data: request,
      }),
  },
  lookupSessions: {
    POST: async (request: schema.GatedLookupSessionsPOSTRequest) =>
      post<rsp.GatedLookupSessionsResponse>({
        url: `/lookup-sessions`,
        data: request,
      }),
  },
  pay: {
    POST: async (request: schema.GatedPayPOSTRequest) =>
      post<rsp.GatedPayPOSTResponse>({
        url: `/pay`,
        data: request,
      }),
  },
  paymentIntent: {
    POST: async (request: schema.GatedPaymentIntentPOSTRequest) =>
      post<rsp.GatedPaymentIntentPOSTResponse>({
        url: `/payment-intent`,
        data: request,
      }),
  },
  promoPayment: {
    POST: async (request: schema.GatedPromoPaymentPOSTRequest) =>
      post<rsp.GatedPromoPaymentResponse>({
        url: `/promo-payment`,
        data: request,
      }),
  },
  promoRegister: {
    POST: async (request: schema.GatedPromoRegisterPOSTRequest) =>
      post<rsp.GatedPromoRegisterResponse>({
        url: `/promo-register`,
        data: request,
      }),
  },
  receipt: {
    POST: async (request: schema.GatedReceiptPOSTRequest) =>
      post<rsp.GatedReceiptPOSTResponse>({
        url: `/receipt`,
        data: request,
      }),
  },
  registerSession: {
    POST: async (request: schema.GatedRegisterSessionPOSTRequest) =>
      post<rsp.GatedRegisterSessionResponse>({
        url: `/register-session`,
        data: request,
      }),
  },
  savedPaymentMethodActions: {
    POST: async (request: schema.GatedSavedPaymentMethodActionsPOSTRequest) =>
      post<
        | schema.GatedSavedPaymentMethodActionsPOSTSuccessResponse
        | schema.GatedSavedPaymentMethodActionsPOSTErrorResponse
      >({
        url: `/saved-payment-method-actions`,
        data: request,
      }),
  },
  savedPaymentMethods: {
    POST: async (request: schema.GatedSavedPaymentMethodsPOSTRequest) =>
      post<
        | schema.GatedSavedPaymentMethodsPOSTSuccessResponse
        | schema.GatedSavedPaymentMethodsPOSTErrorResponse
      >({
        url: `/saved-payment-methods`,
        data: request,
      }),
  },
  sessionDetails: {
    POST: async (request: schema.GatedSessionDetailsPOSTRequest) =>
      post<
        | schema.GatedSessionDetailsPOSTSuccessResponse
        | schema.GatedSessionDetailsPOSTErrorResponse
      >({
        url: "/session-details",
        data: request,
      }),
  },
  setupIntent: {
    POST: async (request: schema.GatedSetupIntentPOSTRequest) =>
      post<rsp.GatedSetupIntentResponse>({
        url: `/setup-intent`,
        data: request,
      }),
  },
  setupIntentWallet: {
    POST: async (request: schema.GatedSetupIntentWalletPOSTRequest) =>
      post<
        | schema.GatedSetupIntentWalletPOSTSuccessResponse
        | schema.GatedSetupIntentWalletPOSTErrorResponse
      >({
        url: `/setup-intent-wallet`,
        data: request,
      }),
  },
  tagCash: {
    POST: async (request: schema.GatedTagCashPOSTRequest) =>
      post<rsp.GatedTagCashPOSTResponse>({
        url: `/tag-cash`,
        data: request,
      }),
  },
  textConfirmation: {
    POST: async (request: schema.GatedTextConfirmationPOSTRequest) =>
      post<rsp.GatedTextConfirmationResponse>({
        url: `/text-confirmation`,
        data: request,
      }),
  },
  textReceipt: {
    POST: async (request: schema.GatedTextReceiptPOSTRequest) =>
      post<rsp.GatedTextReceiptPOSTResponse>({
        url: `/text-receipt`,
        data: request,
      }),
  },
  welcome: {
    POST: async (request: schema.GatedWelcomePOSTRequest) =>
      post<rsp.GatedWelcomePOSTResponse>({
        url: `/welcome`,
        data: request,
      }),
  },
}

export default api
